import type { Session } from '@cobliteam/common-types';
import useAuthStore, { type AuthState } from './useAuthStore';

/**
 * Projeção da fatia `session` do `useAuthStore` unificado. Mantida como compat shim durante a
 * migração das ~10 call-sites que ainda importam `useSessionStore`; novo código deve importar
 * `useAuthStore` diretamente.
 */
export interface SessionState {
  session: Session | null;
  setSession: (session: Session) => void;
  updateSession: (patch: Partial<Session>) => void;
  clearSession: () => void;
}

const projectSessionState = (s: AuthState): SessionState => ({
  session: s.session,
  setSession: s.setSession,
  updateSession: s.updateSession,
  clearSession: s.clearSession,
});

function useSessionStore<T>(selector: (state: SessionState) => T): T {
  return useAuthStore((s) => selector(projectSessionState(s)));
}

useSessionStore.getState = (): SessionState => projectSessionState(useAuthStore.getState());
useSessionStore.setState = (partial: Partial<SessionState>) => {
  // Only session-shaped patches make sense here — keep the shim conservative.
  const patch: Partial<AuthState> = {};
  if ('session' in partial) patch.session = partial.session ?? null;
  useAuthStore.setState(patch);
};

export default useSessionStore;
